import React, { useCallback, useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaEye, FaFileInvoiceDollar, FaMoneyBillWave, FaClock, FaCheckCircle } from 'react-icons/fa';
import { invoiceAPI, studentAPI, invoiceItemAPI, paymentAPI } from '../services/api';
import Modal from '../components/Modal';
import './InvoicesPage.css';

const currency = (amount) => `Rs ${Number(amount || 0).toFixed(2)}`;
const invoicePurposeLabel = (invoice) =>
  invoice?.Description
  || (invoice?.SourceType === 'Product' ? 'Store purchase invoice'
    : invoice?.SourceType === 'Enrollment' ? 'Batch enrollment invoice'
      : invoice?.SourceType ? `${invoice.SourceType} invoice` : 'General invoice');

const itemLabel = (item) =>
  item?.ItemType
  || (item?.ProductID ? `Product ${item.ProductID}` : 'Invoice item');

const buildInvoiceSummary = (rows = []) =>
  rows.reduce(
    (acc, invoice) => {
      const totalAmount = Number(invoice.TotalAmount || 0);
      const amountPaid = Number(invoice.AmountPaid || 0);
      const rawBalance = invoice.BalanceDue ?? (totalAmount - amountPaid);
      const balanceDue = Number(rawBalance || 0);
      const computedStatus =
        invoice.ComputedStatus ||
        (amountPaid >= totalAmount && totalAmount > 0
          ? 'Paid'
          : amountPaid > 0
            ? 'Partial'
            : invoice.Status || 'Pending');

      acc.totalInvoices += 1;
      acc.totalAmount += totalAmount;
      acc.totalPaid += amountPaid;
      acc.totalBalance += Math.max(balanceDue, 0);

      const key = String(computedStatus).toLowerCase();
      acc.statusCounts[key] = (acc.statusCounts[key] || 0) + 1;
      return acc;
    },
    {
      totalInvoices: 0,
      totalAmount: 0,
      totalPaid: 0,
      totalBalance: 0,
      statusCounts: { paid: 0, partial: 0, pending: 0, cancelled: 0 }
    }
  );

function InvoicesPage({
  studentId = null,
  readOnly = false,
  title = 'Invoice Management',
  subtitle = ''
}) {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    StudentID: '',
    InvoiceDate: new Date().toISOString().split('T')[0],
    TotalAmount: '',
    Status: 'Pending',
  });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const invoiceResponse = studentId ? await invoiceAPI.getByStudent(studentId) : await invoiceAPI.getAll();
      const invoiceRows = invoiceResponse.data || [];
      setInvoices(invoiceRows);

      try {
        const summaryResponse = await invoiceAPI.getSummary(studentId ? { studentId } : undefined);
        setSummary(summaryResponse.data || buildInvoiceSummary(invoiceRows));
      } catch (summaryError) {
        if (summaryError?.response?.status === 404) {
          setSummary(buildInvoiceSummary(invoiceRows));
        } else {
          throw summaryError;
        }
      }

      setError('');
    } catch (err) {
      setError('Failed to fetch invoices: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  const fetchStudents = async () => {
    try {
      const response = await studentAPI.getAll();
      setStudents(response.data || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  };

  useEffect(() => {
    fetchInvoices();
    if (!readOnly) {
      fetchStudents();
    }
  }, [fetchInvoices, readOnly]);

  const filteredInvoices = (() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return invoices;

    return invoices.filter((invoice) => {
      const studentName = readOnly
        ? ''
        : (invoice.StudentName || getStudentName(invoice.StudentID)).toLowerCase();
      const invoiceId = String(invoice.InvoiceID || '').toLowerCase();
      const status = String(invoice.ComputedStatus || invoice.Status || '').toLowerCase();
      const description = String(invoicePurposeLabel(invoice)).toLowerCase();
      const dueDate = String(invoice.DueDate || '').toLowerCase();
      return invoiceId.includes(term) || studentName.includes(term) || status.includes(term) || description.includes(term) || dueDate.includes(term);
    });
  })();

  const summaryCards = [
    {
      label: 'Total Invoices',
      value: summary?.totalInvoices || invoices.length,
      icon: <FaFileInvoiceDollar />
    },
    {
      label: 'Amount Billed',
      value: currency(summary?.totalAmount),
      icon: <FaMoneyBillWave />
    },
    {
      label: readOnly ? 'Paid by You' : 'Collected',
      value: currency(summary?.totalPaid),
      icon: <FaCheckCircle />
    },
    {
      label: 'Pending Balance',
      value: currency(summary?.totalBalance),
      icon: <FaClock />
    }
  ];

  const handleOpenModal = (invoice = null) => {
    if (invoice) {
      setEditingId(invoice.InvoiceID);
      const formattedDate = String(invoice.InvoiceDate || '').includes('T')
        ? String(invoice.InvoiceDate).split('T')[0]
        : invoice.InvoiceDate;
      setFormData({
        StudentID: invoice.StudentID || '',
        InvoiceDate: formattedDate || new Date().toISOString().split('T')[0],
        TotalAmount: invoice.TotalAmount || '',
        Status: invoice.Status || invoice.ComputedStatus || 'Pending'
      });
    } else {
      setEditingId(null);
      setFormData({
        StudentID: '',
        InvoiceDate: new Date().toISOString().split('T')[0],
        TotalAmount: '',
        Status: 'Pending',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await invoiceAPI.update(editingId, formData);
      } else {
        await invoiceAPI.create(formData);
      }
      await fetchInvoices();
      handleCloseModal();
      setError('');
    } catch (err) {
      setError('Failed to save invoice: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoiceAPI.delete(id);
        await fetchInvoices();
        setError('');
      } catch (err) {
        setError('Failed to delete invoice: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleViewDetails = async (invoice) => {
    try {
      setSelectedInvoice(invoice);
      const [itemsResponse, paymentsResponse] = await Promise.all([
        invoiceItemAPI.getByInvoice(invoice.InvoiceID),
        paymentAPI.getByInvoice(invoice.InvoiceID)
      ]);
      setInvoiceItems(itemsResponse.data || []);
      setInvoicePayments(paymentsResponse.data || []);
    } catch (err) {
      setInvoiceItems([]);
      setInvoicePayments([]);
      console.error('Failed to fetch invoice details:', err);
    }
  };

  const getStudentName = (targetStudentId) => {
    const student = students.find((item) => item.StudentID === targetStudentId);
    if (!student) return 'N/A';
    return `${student.FirstName || ''} ${student.LastName || ''}`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const currentStatus = (invoice) => invoice?.ComputedStatus || invoice?.Status || 'Pending';

  return (
    <div className="invoices-page admin-module-shell">
      <div className="module-panel">
        <div className="page-header">
          <div>
            <h1 className="page-title">{title}</h1>
            {subtitle && <p className="page-subtitle">{subtitle}</p>}
          </div>
          {!readOnly && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <FaPlus /> New Invoice
            </button>
          )}
        </div>

        <div className="summary-grid">
          {summaryCards.map((card) => (
            <article className="summary-card" key={card.label}>
              <span className="summary-icon">{card.icon}</span>
              <p>{card.label}</p>
              <h3>{card.value}</h3>
            </article>
          ))}
        </div>

        <div className="status-grid">
          <div className="status-card paid">
            <span>Paid</span>
            <strong>{summary?.statusCounts?.paid || 0}</strong>
          </div>
          <div className="status-card partial">
            <span>Partial</span>
            <strong>{summary?.statusCounts?.partial || 0}</strong>
          </div>
          <div className="status-card pending">
            <span>Pending</span>
            <strong>{summary?.statusCounts?.pending || 0}</strong>
          </div>
          <div className="status-card cancelled">
            <span>Cancelled</span>
            <strong>{summary?.statusCounts?.cancelled || 0}</strong>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder={readOnly ? 'Search by invoice, purpose, due date, or status...' : 'Search by invoice, student, or status...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading">Loading invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="no-data">{readOnly ? 'No invoices available in your account yet' : 'No invoices found'}</div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  {!readOnly && <th>Student</th>}
                  <th>Date</th>
                  {readOnly && <th>Purpose</th>}
                  {readOnly && <th>Due Date</th>}
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.InvoiceID}>
                    <td data-label="Invoice ID">
                      <strong>#{invoice.InvoiceID}</strong>
                    </td>
                    {!readOnly && <td data-label="Student">{invoice.StudentName || getStudentName(invoice.StudentID)}</td>}
                    <td data-label="Date">{formatDate(invoice.InvoiceDate)}</td>
                    {readOnly && <td data-label="Purpose">{invoicePurposeLabel(invoice)}</td>}
                    {readOnly && <td data-label="Due Date">{formatDate(invoice.DueDate)}</td>}
                    <td data-label="Total">{currency(invoice.TotalAmount)}</td>
                    <td data-label="Paid">{currency(invoice.AmountPaid)}</td>
                    <td data-label="Balance">{currency(invoice.BalanceDue)}</td>
                    <td data-label="Status">
                      <span className={`status-badge ${currentStatus(invoice).toLowerCase()}`}>
                        {currentStatus(invoice)}
                      </span>
                    </td>
                    <td data-label="Actions" className="action-buttons">
                      <button className="btn btn-small btn-secondary" onClick={() => handleViewDetails(invoice)}>
                        <FaEye /> View
                      </button>
                      {!readOnly && (
                        <>
                          <button className="btn btn-small btn-primary" onClick={() => handleOpenModal(invoice)}>
                            <FaEdit /> Edit
                          </button>
                          <button className="btn btn-small btn-danger" onClick={() => handleDelete(invoice.InvoiceID)}>
                            <FaTrash /> Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!readOnly && (
        <Modal isOpen={isModalOpen} title={editingId ? 'Edit Invoice' : 'New Invoice'} onClose={handleCloseModal}>
          <form onSubmit={handleSubmit} className="invoice-form">
            <div className="form-group">
              <label>Student *</label>
              <select name="StudentID" value={formData.StudentID} onChange={handleInputChange} required>
                <option value="">Select Student</option>
                {students.map((student) => (
                  <option key={student.StudentID} value={student.StudentID}>
                    {`${student.FirstName || ''} ${student.LastName || ''}`.trim()} ({student.StudentID})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Invoice Date</label>
                <input type="date" name="InvoiceDate" value={formData.InvoiceDate} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Total Amount *</label>
                <input type="number" name="TotalAmount" value={formData.TotalAmount} onChange={handleInputChange} step="0.01" required />
              </div>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select name="Status" value={formData.Status} onChange={handleInputChange}>
                <option value="Pending">Pending</option>
                <option value="Partial">Partial</option>
                <option value="Paid">Paid</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update Invoice' : 'Create Invoice'}</button>
              <button type="button" className="btn btn-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      <Modal isOpen={selectedInvoice !== null} title={`Invoice #${selectedInvoice?.InvoiceID || ''}`} onClose={() => setSelectedInvoice(null)}>
        <div className="invoice-details">
          <div className="invoice-detail-grid">
            <article>
              <label>Status</label>
              <p>{selectedInvoice ? currentStatus(selectedInvoice) : 'Pending'}</p>
            </article>
            <article>
              <label>Invoice Date</label>
              <p>{formatDate(selectedInvoice?.InvoiceDate)}</p>
            </article>
            <article>
              <label>Due Date</label>
              <p>{formatDate(selectedInvoice?.DueDate)}</p>
            </article>
            <article>
              <label>Purpose</label>
              <p>{invoicePurposeLabel(selectedInvoice)}</p>
            </article>
            <article>
              <label>Total Amount</label>
              <p>{currency(selectedInvoice?.TotalAmount)}</p>
            </article>
            <article>
              <label>Paid Amount</label>
              <p>{currency(selectedInvoice?.AmountPaid)}</p>
            </article>
            <article>
              <label>Balance Due</label>
              <p>{currency(selectedInvoice?.BalanceDue)}</p>
            </article>
            {!readOnly && (
              <article>
                <label>Student</label>
                <p>{selectedInvoice?.StudentName || getStudentName(selectedInvoice?.StudentID)}</p>
              </article>
            )}
            {!readOnly && (
              <article>
                <label>Payments Recorded</label>
                <p>{selectedInvoice?.PaymentCount || invoicePayments.length}</p>
              </article>
            )}
          </div>

          {readOnly && Number(selectedInvoice?.BalanceDue || 0) > 0 && (
            <p className="empty-note">{`Pending payment: ${currency(selectedInvoice?.BalanceDue)}. Use the Payments tab to complete it.`}</p>
          )}

          <h3>Invoice Items</h3>
          {invoiceItems.length > 0 ? (
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoiceItems.map((item) => (
                  <tr key={item.LineItemID}>
                    <td data-label="Item">{itemLabel(item)}</td>
                    <td data-label="Quantity">{item.Quantity || 0}</td>
                    <td data-label="Unit Price">{currency(item.Price)}</td>
                    <td data-label="Total">{currency(Number(item.Price || 0) * Number(item.Quantity || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-note">No items in this invoice.</p>
          )}

          <h3>Payment History</h3>
          {invoicePayments.length > 0 ? (
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Method</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoicePayments.map((payment) => (
                  <tr key={payment.PaymentID}>
                    <td data-label="Date">{formatDate(payment.PaymentDate)}</td>
                    <td data-label="Method">{payment.PaymentMethod || 'N/A'}</td>
                    <td data-label="Amount">{currency(payment.AmountPaid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty-note">No payments recorded for this invoice.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default InvoicesPage;
